/**
 * Matches Algolia hits against the Liquid-bootstrapped product array,
 * returning Liquid products in Algolia's order.
 *
 * Algolia controls which products appear and in what order.
 * Liquid provides the rich product data (variants, images, metafields, etc.).
 */

/**
 * Builds a lookup map from Liquid products keyed by handle.
 *
 * @param {Object[]} liquidProducts - Products from collection-json.liquid
 * @returns {Map<string, Object>}
 */
function buildProductMap(liquidProducts) {
  const map = new Map();

  for (const product of liquidProducts) {
    if (product.handle) {
      map.set(product.handle, product);
    }
  }

  return map;
}

/**
 * Takes Algolia hits and returns the corresponding Liquid products
 * in Algolia's order. Products that exist in Algolia but not in the
 * Liquid data are skipped (edge case — shouldn't normally happen).
 *
 * Each matched product is enriched with `__algoliaObjectID` and
 * `__algoliaPosition` (1-based) for Algolia Insights tracking.
 *
 * @param {Object[]} algoliaHits - Hits from fetchCollectionProducts()
 * @param {Object[]} liquidProducts - Full product array from collection-json.liquid
 * @returns {Object[]} Liquid products ordered by Algolia, with tracking metadata
 */
export function matchProducts(algoliaHits, liquidProducts) {
  const productMap = buildProductMap(liquidProducts);
  const matched = [];
  let position = 1;

  for (const hit of algoliaHits) {
    const handle = hit.handle;
    const product = handle && productMap.get(handle);

    if (product) {
      matched.push({
        ...product,
        __algoliaObjectID: hit.objectID || null,
        __algoliaPosition: position,
      });
      position++;
    }
  }

  return matched;
}

/**
 * Returns the set of Liquid product handles that were NOT returned
 * by Algolia. Useful for debugging or identifying sync gaps.
 *
 * @param {Object[]} algoliaHits
 * @param {Object[]} liquidProducts
 * @returns {string[]}
 */
export function findUnmatchedProducts(algoliaHits, liquidProducts) {
  const algoliaHandles = new Set(algoliaHits.map((hit) => hit.handle));

  return liquidProducts
    .filter((product) => product.handle && !algoliaHandles.has(product.handle))
    .map((product) => product.handle);
}
